import { Injectable, inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth';

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  /** Requests that must go out without our Bearer token. */
  private readonly skipAuthUrls = [
    'psgc.gitlab.io',
    'login',
    'signup',
    '/identity/forgot-password', // public flow only; admin-forgot-password needs the token
    '/identity/reset-password',
    '/identity/token', // login + refresh-token
    '/b1s/v1/Login',
  ];

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.skipAuthUrls.some((url) => request.url.includes(url))) {
      return next.handle(request);
    }

    // SAP Business One requests use their own session token.
    if (request.url.includes('/b1s/v1/')) {
      const sapToken = sessionStorage.getItem('SapToken');
      if (sapToken) {
        request = request.clone({ setHeaders: { Authorization: sapToken } });
      }
      return next.handle(this.withJsonContentType(request));
    }

    // The token lives inside the `currentUser` object saved by AuthService.
    const token = this.authService.currentUserValue?.jwToken ?? null;

    if (token) {
      if (this.authService.isAuthenticated) {
        request = request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
      } else {
        // Token expired: end the session instead of sending a dead token.
        this.endSession();
      }
    }

    return next.handle(this.withJsonContentType(request)).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          this.endSession();
        }
        // Re-throw so the component can still show its own error message.
        return throwError(() => error);
      }),
    );
  }

  private withJsonContentType(request: HttpRequest<any>): HttpRequest<any> {
    // Leave FormData uploads alone: the browser must set the multipart boundary itself.
    if (request.headers.has('Content-Type') || request.body instanceof FormData) {
      return request;
    }
    return request.clone({ setHeaders: { 'Content-Type': 'application/json' } });
  }

  private endSession(): void {
    this.authService.clearSession();
    this.router.navigate(['/']);
  }
}